// The bands of the lasting effects, in the padding of the board frame. An
// effect that binds one side lies along the home edge of that side. An effect
// that binds both goes all the way round. Each band is solid next to the
// board, then half dither, then quarter dither, as the focus mark of a square
// is. Two effects on one edge share it in lanes, and the effect of both sides
// is next to the board. The bands touch no square, so they do not fight the
// marks, the terrain, the covers, or the pitch arrows. `game/lasting.ts` is the
// table. Added 2026-10-14 from a design pass. Garrett: "I think the band is
// strong enough", about a frame of 12px on a wide screen and about 6px on a
// phone. A field of specks over the squares was the other option.
// A band comes in steps, from a quarter dither to its full look, and goes the
// same way back, as a cloud thins. It changes on the beat of the change, see
// `state/lastingShown.ts`.

import {
  BOARD_SIZE,
  LASTING_BAND_STEP_MS,
  markDitherId,
  REDUCED_MOTION_QUERY,
  SQUARE_PX,
} from '../constants';
import type { Side } from '../engine/types';
import {
  LASTING,
  type Lasting,
  type LastingCounts,
  type LastingKey,
  sidesBound,
} from '../game/lasting';
import { svg } from '../lib/svg';
import { isReviewing } from '../state/history';
import { shownLasting, subscribeLasting } from '../state/lastingShown';
import { localSide, localSideStore } from '../state/localSide';

const BOARD_PX = BOARD_SIZE * SQUARE_PX;
/** The paints from the strongest. A part of a band that thins takes the next paint, and after the last it is gone. */
const PAINTS = ['solid', 'half', 'quarter'] as const;
/** The parts of a lane from the board out, as shares of its width. Each starts at its own paint. */
const BAND_SHARES = [0.4, 0.3, 0.3] as const;
/** A band thinned this many steps has no part left. */
const THIN_GONE = PAINTS.length;
/** A band that comes starts with only its first part, as a quarter dither. */
const THIN_START = THIN_GONE - 1;

type Edge = 'top' | 'bottom' | 'left' | 'right';
const ALL_EDGES: readonly Edge[] = ['top', 'bottom', 'left', 'right'];

/** The edges of the frame that `lasting` lies on. The side of this screen is at the bottom. */
const edgesOf = (counts: LastingCounts, lasting: Lasting): readonly Edge[] => {
  const sides: Side[] = sidesBound(counts, lasting);
  if (sides.length === 0) return [];
  if (sides.length > 1) return ALL_EDGES;
  return [sides[0] === localSide() ? 'bottom' : 'top'];
};

/** One part of a band on one edge, in board units. `from` is its distance from the board. `wraps` stretches it round the corners. */
const bandRect = (
  edge: Edge,
  from: number,
  width: number,
  wraps: boolean,
): [number, number, number, number] => {
  const reach = wraps ? from + width : 0;
  switch (edge) {
    case 'top':
      return [-reach, -from - width, BOARD_PX + reach * 2, width];
    case 'bottom':
      return [-reach, BOARD_PX + from, BOARD_PX + reach * 2, width];
    case 'left':
      return [-from - width, -from, width, BOARD_PX + from * 2];
    case 'right':
      return [BOARD_PX + from, -from, width, BOARD_PX + from * 2];
  }
};

/** A band on the frame: its edges, how many steps it is thinned now, and how many it goes to. */
type Band = { lasting: Lasting; edges: readonly Edge[]; thin: number; target: number };

export const createLastingBands = (board: SVGSVGElement): SVGSVGElement => {
  const root = svg('svg', { class: 'lasting-bands', 'aria-hidden': 'true' });
  const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);
  const bands = new Map<LastingKey, Band>();
  let stepTimer: ReturnType<typeof setTimeout> | null = null;

  /** Sizes the view box so one unit is one unit of the board, and the board is at 0 0. */
  const fitToBoard = (): number | null => {
    const frame = root.parentElement;
    if (!frame) return null;
    const outer = frame.getBoundingClientRect();
    const inner = board.getBoundingClientRect();
    if (inner.width === 0) return null;
    const scale = BOARD_PX / inner.width;
    const left = (inner.left - outer.left - frame.clientLeft) * scale;
    const top = (inner.top - outer.top - frame.clientTop) * scale;
    root.setAttribute(
      'viewBox',
      `${-left} ${-top} ${frame.clientWidth * scale} ${frame.clientHeight * scale}`,
    );
    // The padding of the frame is the room for the bands.
    return Math.min(left, top);
  };

  const render = (): void => {
    const pad = fitToBoard();
    if (pad === null) return;
    // An effect of both sides is next to the board, then the effects of one side. A band that goes keeps its lane.
    const shown = [...bands.values()].sort((a, b) => b.edges.length - a.edges.length);
    const lane = pad / Math.max(1, shown.length);
    const parts: SVGRectElement[] = [];
    shown.forEach(({ lasting, edges, thin }, index) => {
      const wraps = edges.length === ALL_EDGES.length;
      let from = index * lane;
      BAND_SHARES.forEach((share, part) => {
        const width = lane * share;
        const paint = PAINTS[part + thin];
        if (paint) {
          const fill =
            paint === 'solid'
              ? `var(--lasting-${lasting.polarity})`
              : `url(#${markDitherId(lasting.polarity, paint)})`;
          for (const edge of edges) {
            const [x, y, w, h] = bandRect(edge, from, width, wraps);
            parts.push(svg('rect', { x, y, width: w, height: h, style: `fill:${fill}` }));
          }
        }
        from += width;
      });
    });
    root.replaceChildren(...parts);
  };

  /** Moves each band one step to its target, and again after a step while one has not arrived. */
  const step = (): void => {
    stepTimer = null;
    for (const [key, band] of bands) {
      if (band.thin !== band.target) band.thin += Math.sign(band.target - band.thin);
      if (band.thin >= THIN_GONE) bands.delete(key);
    }
    render();
    if ([...bands.values()].some((band) => band.thin !== band.target))
      stepTimer = setTimeout(step, LASTING_BAND_STEP_MS);
  };

  const sync = (): void => {
    const counts = shownLasting();
    // An earlier step, or a reader who asks for less motion, sees the bands with no steps.
    const jumps = isReviewing() || reducedMotion.matches;
    for (const lasting of LASTING) {
      const edges = edgesOf(counts, lasting);
      const band = bands.get(lasting.key);
      if (edges.length > 0) {
        if (band) {
          band.edges = edges;
          band.target = 0;
        } else {
          bands.set(lasting.key, { lasting, edges, thin: jumps ? 0 : THIN_START, target: 0 });
        }
      } else if (band) {
        band.target = THIN_GONE;
        if (jumps) bands.delete(lasting.key);
      }
    }
    render();
    if (!stepTimer && [...bands.values()].some((band) => band.thin !== band.target))
      stepTimer = setTimeout(step, LASTING_BAND_STEP_MS);
  };

  // The frame changes size with the window and the layout.
  const watch = new ResizeObserver(render);
  queueMicrotask(() => {
    if (root.parentElement) watch.observe(root.parentElement);
    sync();
  });
  subscribeLasting(sync);
  localSideStore.subscribe(sync);
  return root;
};
